import { z } from 'zod';
import { SupportedCountry } from '@/constants/country';
import { PRODUCT_PERMISSION_SCOPE_CODES } from '@/features/permission-and-role/constants/scope-code';
import type { SupportedLanguage } from '@/lib/i18n';

// UserSessionのSchema
export const userSessionSchema = z.object({
  /**
   * @see https://standandforce.slack.com/archives/C075SH8PNPR/p1732694875732869?thread_ts=1732665610.964199&cid=C075SH8PNPR
   * @note 未対応のlocaleが指定された場合、en-USにフォールバック
   */
  hasServiceAccount: z.boolean(),
  hasOperator: z.boolean(),
  hasIndividual: z.boolean(),
  /**
   * 以下のようにしてしまうと、バックエンドで返却される Code が増えた時に Zod の Validation エラーになってしまう
   * hasPermissionScopes: z.array(z.nativeEnum(PRODUCT_PERMISSION_SCOPE_CODES)),
   */
  hasPermissionScopes: z.unknown().transform(input => Array.isArray(input) ? input.filter(val => Object.values(PRODUCT_PERMISSION_SCOPE_CODES).includes(val)) : []).pipe(z.array(z.nativeEnum(PRODUCT_PERMISSION_SCOPE_CODES))),
  validTo: z.string().nullish(),
  // 🐕
  locale: z.custom<SupportedLanguage>().catch('en-US' as const).default('en-US' as const),
  user: z.object({
    id: z.string(),
    identityId: z.string(),
    email: z.string()
  }),
  organization: z.object({
    tenantId: z.string(),
    organizationId: z.string(),
    primaryLocale: z.custom<SupportedLanguage>().catch('en-US' as const).default('en-US' as const),
    tenantName: z.string(),
    organizationName: z.string(),
    location: z.nativeEnum(SupportedCountry),
    locationFlagUrl: z.string()
  }),
  switchableOrganizations: z.array(z.object({
    sessionType: z.enum(['MEMBERSHIP', 'SERVICE_ACCOUNT'])
    // API 後方互換性のために optional 指定。
    // sessionType を返す backend がリリースされた後は🐕全て消してOK
    .default('MEMBERSHIP'),
    // 🐕
    tenantId: z.string(),
    organizationId: z.string(),
    tenantName: z.string(),
    organizationName: z.string(),
    isDifferentTenant: z.boolean().optional(),
    // 🐕
    personName: z.string().optional(),
    // 🐕
    workerPrimaryEmail: z.string().optional() // 🐕
  })),
  serviceAccounts: z.array(z.any()).optional(),
  // 🐕
  person: z.object({
    personId: z.string(),
    name: z.string(),
    avatarUrl: z.string().optional()
  }),
  featureFlags: z.record(z.string(), z.union([z.number(), z.string(), z.boolean()])).default({}),
  /**
   * メンバーポータルで使用可能なデータスコープ（`(台帳, scope)`）の一覧（DC-8324）。
   * hasPermissionScopes と同じくセッションに畳み込まれる（旧 GET /v2/self-service/data-scopes
   * を廃止しセッション取得へ統合）。individual セッションのみ算出され、それ以外は空配列。
   * 値の SSoT は backend レジストリで、ラベルは FE 辞書で解決するため文字列で受ける。
   */
  dataScopes: z.array(z.object({
    ledger: z.string(),
    scope: z.string()
  })).default([])
});

// AuthStoreのSchema
export const authStoreSchema = z.object({
  session: userSessionSchema.nullable(),
  locale: z.custom<SupportedLanguage>().nullable(),
  setLocale: z.function(z.tuple([z.custom<SupportedLanguage>()])).returns(z.void()),
  isAuthenticated: z.function().returns(z.promise(z.boolean())),
  refreshUserSession: z.function().returns(z.promise(userSessionSchema)),
  getSession: z.function().returns(z.promise(userSessionSchema)),
  downloadSignedCookies: z.function().returns(z.promise(z.void()))
});